import { DataTablePassThroughOptions } from 'primereact/datatable'
import clsx from 'clsx'

export const dataTableConfig: DataTablePassThroughOptions = {
  // Main container
  root: ({ props }: { props: any }) => ({
    className: clsx('mk-datatable', {
      'mk-datatable--gridlines': props.showGridlines,
      'mk-datatable--striped': props.stripedRows,
      'mk-datatable--sm': props.size === 'small',
      'mk-datatable--lg': props.size === 'large',
    })
  }),

  // Loading states
  loadingOverlay: {
    className: 'bg-white/70 absolute flex items-center justify-center z-10 w-full h-full'
  },
  loadingIcon: {
    className: 'w-8 h-8 animate-spin'
  },

  // Main structure
  wrapper: {
    className: 'overflow-auto'
  },
  header: {
    className: 'bg-white p-4 border-b border-gray-200 font-bold'
  },
  table: {
    className: 'w-full border-collapse'
  },
  thead: {
    className: 'bg-gray-50'
  },
  headerGroup: {
    className: 'bg-gray-50'
  },
  tbody: {
    className: ''
  },
  tfoot: {
    className: 'bg-gray-50'
  },

  // Rows and cells
  headerRow: {
    className: 'border-b border-gray-200'
  },
  headerCell: ({ props }: { props: any }) => ({
    className: clsx('p-3 text-left font-semibold overflow-hidden text-overflow-ellipsis', {
      'cursor-pointer': props.sortable,
      'select-none': props.sortable || props.resizeable,
    })
  }),
  columnResizer: {
    className: 'absolute top-0 right-0 h-full w-2 cursor-col-resize block'
  },
  bodyRow: ({ context }: { context: any }) => ({
    className: clsx('border-b border-gray-200 transition-colors', {
      'hover:bg-gray-50': !context.selected,
      'bg-blue-50': context.selected,
      'bg-gray-100': context.stripedRows && context.index % 2 === 1,
    })
  }),
  bodyCell: {
    className: 'p-3 overflow-hidden text-overflow-ellipsis'
  },
  rowExpansionToggler: {
    className: 'inline-flex items-center justify-center w-6 h-6 rounded-full cursor-pointer hover:bg-gray-200'
  },
  rowGroupToggler: {
    className: 'inline-flex items-center justify-center w-6 h-6 rounded-full cursor-pointer hover:bg-gray-200'
  },
  rowGroupHeader: {
    className: 'bg-gray-100 font-bold p-3'
  },
  rowGroupFooter: {
    className: 'bg-gray-50 p-3'
  },
  rowGroupHeaderCell: {
    className: 'p-3 text-left'
  },
  rowGroupFooterCell: {
    className: 'p-3 text-left font-semibold'
  },
  footerRow: {
    className: 'border-t border-gray-200'
  },
  footerCell: {
    className: 'p-3 text-left font-semibold'
  },

  // Empty state
  emptyMessage: {
    className: 'p-6 text-center text-gray-500'
  },

  // Pagination
  paginatorTop: {
    className: 'border-b border-gray-200'
  },
  paginatorBottom: {
    className: 'border-t border-gray-200'
  },
  paginator: {
    className: 'flex items-center justify-between p-3'
  },
  paginatorLeft: {
    className: 'flex items-center'
  },
  paginatorRight: {
    className: 'flex items-center'
  },
  paginatorDropdown: {
    className: 'mx-2'
  },
  paginatorButton: ({ context }: { context: any }) => ({
    className: clsx('inline-flex items-center justify-center w-8 h-8 rounded-full mx-1', {
      'bg-blue-500 text-white': context.active,
      'hover:bg-gray-200 cursor-pointer': !context.disabled && !context.active,
      'opacity-50 cursor-not-allowed': context.disabled,
    })
  }),
  paginatorFirstPageButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full mx-1 hover:bg-gray-200'
  },
  paginatorPrevPageButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full mx-1 hover:bg-gray-200'
  },
  paginatorNextPageButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full mx-1 hover:bg-gray-200'
  },
  paginatorLastPageButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full mx-1 hover:bg-gray-200'
  },

  // Sorting
  sortBadge: {
    className: 'inline-flex items-center justify-center ml-1 w-5 h-5 text-xs rounded-full bg-blue-500 text-white'
  },
  sortIcon: ({ context }: { context: any }) => ({
    className: clsx('ml-1', {
      'text-blue-500': context.sorted,
    })
  }),

  // Filtering
  filterInput: {
    className: 'p-2 border border-gray-300 rounded w-full'
  },
  filterOverlay: {
    className: 'bg-white rounded shadow-lg p-4 absolute z-10'
  },
  filterMatchModeDropdown: {
    className: 'mb-2'
  },
  filterRowItems: {
    className: 'space-y-2'
  },
  filterRowItem: {
    className: 'flex items-center'
  },
  filterSeparator: {
    className: 'my-2 border-t border-gray-200'
  },
  filterButtons: {
    className: 'flex justify-end space-x-2 mt-4'
  },
  filterClearButton: {
    className: 'px-3 py-1 border border-gray-300 rounded hover:bg-gray-100'
  },
  filterApplyButton: {
    className: 'px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600'
  },
  filterMenuButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200 ml-1'
  },
  filterOperator: {
    className: 'mb-2'
  },
  filterConstraints: {
    className: 'space-y-2'
  },
  filterConstraint: {
    className: 'space-y-2'
  },
  filterAddRule: {
    className: 'inline-flex items-center px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 mt-2'
  },

  // Selection
  checkboxWrapper: {
    className: 'inline-flex items-center justify-center'
  },
  checkbox: ({ context }: { context: any }) => ({
    className: clsx('w-5 h-5 border border-gray-300 rounded', {
      'bg-blue-500 border-blue-500': context.checked,
    })
  }),
  checkboxIcon: {
    className: 'text-white'
  },

  // Editing
  rowEditor: {
    className: 'flex items-center space-x-1'
  },
  rowEditorInitButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200'
  },
  rowEditorSaveButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-green-200 text-green-600'
  },
  rowEditorCancelButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-red-200 text-red-600'
  },

  // Column resizing and reordering
  resizeHelper: {
    className: 'absolute top-0 w-px h-full bg-blue-500'
  },
  reorderIndicatorUp: {
    className: 'absolute w-2 h-2 border-l-2 border-t-2 border-blue-500 -mt-1 rotate-45'
  },
  reorderIndicatorDown: {
    className: 'absolute w-2 h-2 border-l-2 border-t-2 border-blue-500 -mb-1 rotate-225'
  },

  // Column toggler
  columnToggler: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200'
  },
  columnTogglerList: {
    className: 'bg-white rounded shadow-lg p-4 absolute z-10'
  },
  columnTogglerListItem: {
    className: 'flex items-center p-2 hover:bg-gray-100 cursor-pointer'
  },

  // Export
  exportButton: {
    className: 'inline-flex items-center px-3 py-1 border border-gray-300 rounded hover:bg-gray-100'
  },

  // Context menu
  contextMenuButton: {
    className: 'inline-flex items-center justify-center w-8 h-8 rounded-full hover:bg-gray-200'
  },

  // Frozen columns
  frozenCell: {
    className: 'sticky bg-inherit z-10 shadow-[1px_0_0_0_#e5e7eb] overflow-hidden text-overflow-ellipsis whitespace-nowrap'
  },

  // Scrollable
  scrollableView: {
    className: 'overflow-auto'
  },
  scrollableHeader: {
    className: 'sticky top-0 z-20 bg-gray-50'
  },
  scrollableBody: {
    className: 'overflow-y-scroll'
  },
  scrollableFooter: {
    className: 'sticky bottom-0 z-20 bg-gray-50'
  },

  // Virtual scroller
  virtualScroller: {
    className: 'overflow-auto'
  },
  virtualScrollerLoader: {
    className: 'flex items-center justify-center p-3'
  },

  // Responsive
  responsiveLayout: {
    className: ''
  },

  // Expanded rows
  rowExpansion: {
    className: 'bg-gray-50 p-3'
  },

  // Drag and drop
  dragHandle: {
    className: 'cursor-move'
  }
}
